import { spawn } from 'node:child_process';
import { createHash } from 'node:crypto';
import { posix as pathPosix } from 'node:path';
import ExcelJS from 'exceljs';
import { XMLParser } from 'fast-xml-parser';
import JSZip from 'jszip';
import mammoth from 'mammoth';
import TurndownService from 'turndown';
import { gfm } from 'turndown-plugin-gfm';
import z from 'zod';
import { UserError } from '../../errors';
import type {
  Model,
  ModelMetadata,
  ModelResponse,
  ModelSettings,
} from '../../model';
import type { RunContext } from '../../run-context';
import {
  type Tool,
  type ToolCallDetails,
  type ToolDualResult,
  type ToolErrorResult,
  tool,
  toolError,
  toolResult,
} from '../../tool';
import type { ToolOutputImage } from '../../types/protocol';
import { combineAbortSignals } from '../../utils/abort-signals';
import { prependBullets } from '../../utils/prepend-bullets';
import { withSandboxSpan } from '../runtime/spans';
import type {
  EditFileArgs,
  GlobFilesArgs,
  GlobFilesResult,
  GrepFilesArgs,
  GrepFilesResult,
  ListDirectoryArgs,
  ReadTextFileResult,
  SandboxDirectoryEntry,
  SandboxSession,
  StatFileResult,
  ViewImageArgs,
  WriteFileArgs,
} from '../session';
import {
  DEFAULT_VIEW_IMAGE_MAX_BYTES,
  DEFAULT_VIEW_IMAGE_MAX_HEIGHT,
  DEFAULT_VIEW_IMAGE_MAX_PIXELS,
  DEFAULT_VIEW_IMAGE_MAX_WIDTH,
  type ViewImageMetadata,
} from '../shared/media';
import {
  Capability,
  type ConfigureCapabilityTools,
  requireBoundSession,
} from './base';

export type ViewImageCaptionerArgs = {
  /**
   * 视觉 fallback 模型实例。文本主模型调用 view_image 时，由它按 focus 生成
   * 图片信息文本。调用侧负责实例化；capability 不做 provider/名字解析。
   */
  model: Model;
  /** captioner 模型调用设置。默认 maxTokens 1000、temperature 0、不开推理。 */
  modelSettings?: ModelSettings;
  /** captioner 单次调用超时（毫秒）。默认 60s。 */
  timeoutMs?: number;
};

export type FilesystemArgs = {
  configureTools?: ConfigureCapabilityTools;
  maxReadBytes?: number;
  maxGlobResults?: number;
  /**
   * view_image 的视觉 fallback captioner。仅当主模型的工具结果通道送不了
   * 图片时生效：view_image 的 schema 变为 { path, focus }（focus 必填），
   * 图片交给该视觉模型按 focus 提取信息，以文本写回工具结果。
   * 原生视觉模型不受影响；默认不配置（文本模型调用 view_image 报不支持）。
   */
  viewImageCaptioner?: ViewImageCaptionerArgs;
  /**
   * 允许 Read 工具读取 .xlsx/.xlsm 表格：解析为 CSV 文本返回。
   * 这是为没有 Bash 工具的沙盒准备的妥协回退路径；默认关闭。
   */
  readXlsx?: boolean;
  /**
   * 允许 Read 工具读取带文本层的 PDF：解析为带页标记的纯文本返回。
   * 扫描件/OCR 第一版不支持；默认关闭。
   */
  readPdf?: boolean;
  /**
   * 允许 Read 工具读取 .docx 文档：使用 Mammoth 抽取为 Markdown 风格文本。
   * 图片/OCR/嵌入对象第一版不支持；默认关闭。
   */
  readDocx?: boolean;
  /**
   * 允许 Read 工具读取 .pptx 演示文稿：抽取 slide 正文与 notes 文本。
   * 母版、批注、图片/OCR、图表/SmartArt 第一版不支持；默认关闭。
   */
  readPptx?: boolean;
  /** Office 文档抽取的输入大小上限。默认 30MB。 */
  officeMaxInputBytes?: number;
  /** PDF 抽取子进程超时时间，单位毫秒。默认 30s。 */
  pdfTimeoutMs?: number;
  /** Python 可执行文件路径。默认读取 LYRA_PDF_PYTHON，未设置则用 python3。 */
  pdfPythonCommand?: string;
  /** @internal 测试注入点。 */
  pdfExtractor?: PdfTextExtractor;
  /** @internal 测试注入点。 */
  docxExtractor?: OfficeTextExtractor;
  /** @internal 测试注入点。 */
  pptxExtractor?: OfficeTextExtractor;
};

type ViewImageToolResult =
  | ToolOutputImage
  | ToolDualResult<ToolOutputImage>
  | string;
type ViewImageLimits = {
  maxImageWidth: number;
  maxImageHeight: number;
  maxImagePixels: number;
  maxImageBytes: number;
};

const DEFAULT_MAX_READ_BYTES = 256 * 1024;
const DEFAULT_MAX_GLOB_RESULTS = 100;
const DEFAULT_VIEW_IMAGE_CAPTIONER_TIMEOUT_MS = 60_000;
const VIEW_IMAGE_CAPTIONER_ERROR_MESSAGE_MAX_CHARS = 200;
const DEFAULT_PDF_TIMEOUT_MS = 30_000;
const DEFAULT_OFFICE_MAX_INPUT_BYTES = 30 * 1024 * 1024;
const PDF_EXTRACT_MAX_OUTPUT_BYTES = 64 * 1024 * 1024;
const FILESYSTEM_READ_STATE_KEY = Symbol('sandbox.filesystem.readState');
const SESSION_READ_STATE_IDS = new WeakMap<object, number>();
let nextSessionReadStateId = 1;

type FilesystemReadState = {
  content: string;
  mtimeMs: number;
  offset?: number;
  limit?: number;
  isPartialView?: boolean;
};

type FilesystemReadStateStore = Map<string, FilesystemReadState>;

type PdfTextExtractor = (args: {
  bytes: Buffer;
  pythonCommand: string;
  timeoutMs: number;
}) => Promise<string>;

type OfficeTextExtractor = (args: {
  bytes: Buffer;
}) => Promise<ExtractedDocumentText>;

type ExtractedDocumentText = {
  text: string;
  warnings?: string[];
};

type OfficeDocumentFormat = 'docx' | 'pptx';

const DEFAULT_FILESYSTEM_TOOL_NAMES = new Set([
  'Read',
  'Write',
  'Edit',
  'Grep',
  'Glob',
  'LS',
  'view_image',
]);

class FilesystemCapability extends Capability {
  readonly type = 'filesystem';
  private readonly configureTools?: ConfigureCapabilityTools;
  private readonly maxReadBytes: number;
  private readonly maxGlobResults: number;
  private readonly viewImageCaptioner?: ViewImageCaptionerArgs;
  private readonly readXlsx: boolean;
  private readonly readPdf: boolean;
  private readonly readDocx: boolean;
  private readonly readPptx: boolean;
  private readonly officeMaxInputBytes: number;
  private readonly pdfTimeoutMs: number;
  private readonly pdfPythonCommand: string;
  private readonly pdfExtractor: PdfTextExtractor;
  private readonly docxExtractor: OfficeTextExtractor;
  private readonly pptxExtractor: OfficeTextExtractor;
  private exposedToolNames?: Set<string>;

  constructor(args: FilesystemArgs = {}) {
    super();
    this.configureTools = args.configureTools;
    this.maxReadBytes = args.maxReadBytes ?? DEFAULT_MAX_READ_BYTES;
    this.maxGlobResults = args.maxGlobResults ?? DEFAULT_MAX_GLOB_RESULTS;
    this.viewImageCaptioner = args.viewImageCaptioner;
    this.readXlsx = args.readXlsx ?? false;
    this.readPdf = args.readPdf ?? false;
    this.readDocx = args.readDocx ?? false;
    this.readPptx = args.readPptx ?? false;
    this.officeMaxInputBytes =
      args.officeMaxInputBytes ?? DEFAULT_OFFICE_MAX_INPUT_BYTES;
    this.pdfTimeoutMs = args.pdfTimeoutMs ?? DEFAULT_PDF_TIMEOUT_MS;
    this.pdfPythonCommand =
      args.pdfPythonCommand ?? process.env.LYRA_PDF_PYTHON ?? 'python3';
    this.pdfExtractor = args.pdfExtractor ?? extractPdfTextWithPython;
    this.docxExtractor = args.docxExtractor ?? extractDocxText;
    this.pptxExtractor = args.pptxExtractor ?? extractPptxText;
  }

  /**
   * 读取 .xlsx/.xlsm 表格并解析为 CSV 文本。
   * - 多个工作表拼接为单个 CSV，用 `===SHEET: 名称===` 围栏分隔。
   * - 渲染结果不超过 maxReadBytes 时直接内联返回。
   * - 超出时落盘到工作区 `.tmp/sheets/` 下，仅返回前若干行预览 + 提醒，
   *   由 agent 后续用 Read/Grep 按需读取完整内容。
   * - 刻意不写入 read-state：xlsx 为只读，不能被 Edit/Write。
   */
  private async readSpreadsheetAsCsv(
    session: SandboxSession,
    filePath: string,
  ): Promise<string> {
    try {
      return await withSandboxSpan(
        'sandbox.read_file',
        {
          path: filePath,
          format: 'xlsx',
          run_as: this._runAs,
        },
        async () => {
          const statResult = await session.statFile!({
            path: filePath,
            runAs: this._runAs,
          });
          if (!statResult.exists) {
            throw new UserError(`文件不存在：${filePath}`);
          }
          if (!statResult.isFile) {
            throw new UserError(`路径不是文件：${filePath}`);
          }
          const raw = await session.readFile!({
            path: filePath,
            runAs: this._runAs,
          });
          const bytes =
            typeof raw === 'string'
              ? Buffer.from(raw, 'utf8')
              : Buffer.from(raw);
          const csv = await renderWorkbookCsv(bytes);
          if (csv.totalLines === 0) {
            return '<system-reminder>警告：工作簿为空，没有可读取的内容。</system-reminder>';
          }
          if (Buffer.byteLength(csv.content, 'utf8') <= this.maxReadBytes) {
            return csv.content;
          }
          const spillPath = buildSpreadsheetSpillPath(
            filePath,
            statResult.mtimeMs,
          );
          await session.writeFile!({
            path: spillPath,
            content: csv.content,
            runAs: this._runAs,
          } satisfies WriteFileArgs);
          return renderSpreadsheetSpillResult(spillPath, csv);
        },
      );
    } catch (error) {
      return renderFilesystemError('读取文件', filePath, error);
    }
  }

  /**
   * 读取有文本层的 PDF 并渲染为 Read 工具的行号文本。
   * - 每页由 Python/PyMuPDF 转为纯文本，并插入 `=== Page N ===` 页标记。
   * - offset/limit 继续表示抽取后文本的行号窗口。
   * - 未传 offset/limit 且文本超出 maxReadBytes 时，落盘到 `.tmp/documents/`。
   * - 刻意不写入 read-state：PDF 为只读抽取结果，不能被 Edit/Write。
   */
  private async readPdfAsText(
    session: SandboxSession,
    filePath: string,
    offset: number | undefined,
    limit: number | undefined,
  ): Promise<string> {
    try {
      return await withSandboxSpan(
        'sandbox.read_file',
        {
          path: filePath,
          format: 'pdf',
          offset,
          limit,
          run_as: this._runAs,
        },
        async () => {
          const statResult = await session.statFile!({
            path: filePath,
            runAs: this._runAs,
          });
          if (!statResult.exists) {
            throw new UserError(`文件不存在：${filePath}`);
          }
          if (!statResult.isFile) {
            throw new UserError(`路径不是文件：${filePath}`);
          }
          const raw = await session.readFile!({
            path: filePath,
            runAs: this._runAs,
          });
          const bytes =
            typeof raw === 'string'
              ? Buffer.from(raw, 'utf8')
              : Buffer.from(raw);
          const text = await this.pdfExtractor({
            bytes,
            pythonCommand: this.pdfPythonCommand,
            timeoutMs: this.pdfTimeoutMs,
          });
          if (text.trim().length === 0) {
            return '<system-reminder>警告：PDF 没有检测到可抽取的文本层。扫描件或图片型 PDF 暂不支持 OCR。</system-reminder>';
          }

          const normalized = normalizeTextForRead(text);
          if (
            offset === undefined &&
            limit === undefined &&
            Buffer.byteLength(normalized, 'utf8') > this.maxReadBytes
          ) {
            const spillPath = buildDocumentSpillPath(
              filePath,
              statResult.mtimeMs,
              'txt',
            );
            await session.writeFile!({
              path: spillPath,
              content: normalized,
              runAs: this._runAs,
            } satisfies WriteFileArgs);
            return renderPdfSpillResult(spillPath, normalized);
          }

          return renderReadFileResult(
            readTextRangeFromStringForTool(filePath, normalized, {
              offset,
              limit,
              mtimeMs: statResult.mtimeMs,
            }),
          );
        },
      );
    } catch (error) {
      return renderFilesystemError('读取文件', filePath, error);
    }
  }

  /**
   * 读取 OOXML Office 文档并渲染为 Read 工具的行号文本。
   * - docx 使用 Mammoth 转 HTML 后再转 Markdown 风格文本。
   * - pptx 只抽取 slide 正文与 notes 文本，不解媒体/母版/批注。
   * - offset/limit 继续表示抽取后文本的行号窗口。
   * - 未传 offset/limit 且文本超出 maxReadBytes 时，落盘到 `.tmp/documents/`。
   * - 刻意不写入 read-state：Office 原文件为只读抽取结果，不能被 Edit/Write。
   */
  private async readOfficeDocumentAsText(
    session: SandboxSession,
    filePath: string,
    format: OfficeDocumentFormat,
    offset: number | undefined,
    limit: number | undefined,
  ): Promise<string> {
    try {
      return await withSandboxSpan(
        'sandbox.read_file',
        {
          path: filePath,
          format,
          offset,
          limit,
          run_as: this._runAs,
        },
        async () => {
          const statResult = await session.statFile!({
            path: filePath,
            runAs: this._runAs,
          });
          if (!statResult.exists) {
            throw new UserError(`文件不存在：${filePath}`);
          }
          if (!statResult.isFile) {
            throw new UserError(`路径不是文件：${filePath}`);
          }
          if (statResult.size > this.officeMaxInputBytes) {
            throw new UserError(
              `${format.toUpperCase()} 文件超过解析上限（${this.officeMaxInputBytes} 字节）。`,
            );
          }

          const raw = await session.readFile!({
            path: filePath,
            maxBytes: this.officeMaxInputBytes,
            runAs: this._runAs,
          });
          const bytes =
            typeof raw === 'string'
              ? Buffer.from(raw, 'utf8')
              : Buffer.from(raw);
          const extractor =
            format === 'docx' ? this.docxExtractor : this.pptxExtractor;
          const extracted = await extractor({ bytes });
          const normalized = normalizeExtractedDocumentText(extracted.text);
          if (normalized.trim().length === 0) {
            return emptyOfficeDocumentReminder(format);
          }

          if (
            offset === undefined &&
            limit === undefined &&
            Buffer.byteLength(normalized, 'utf8') > this.maxReadBytes
          ) {
            const spillPath = buildDocumentSpillPath(
              filePath,
              statResult.mtimeMs,
              'txt',
            );
            await session.writeFile!({
              path: spillPath,
              content: normalized,
              runAs: this._runAs,
            } satisfies WriteFileArgs);
            return renderDocumentSpillResult(
              format.toUpperCase(),
              spillPath,
              normalized,
              extracted.warnings,
            );
          }

          return appendExtractionWarnings(
            renderReadFileResult(
              readTextRangeFromStringForTool(filePath, normalized, {
                offset,
                limit,
                mtimeMs: statResult.mtimeMs,
              }),
            ),
            format.toUpperCase(),
            extracted.warnings,
          );
        },
      );
    } catch (error) {
      return renderFilesystemError('读取文件', filePath, error);
    }
  }

  override tools(): Tool<any>[] {
    const session = requireBoundSession(this.type, this._session);
    const tools: Tool<any>[] = [
      tool({
        name: 'Read',
        description: renderReadToolDescription({
          readPdf: this.readPdf,
          readDocx: this.readDocx,
          readPptx: this.readPptx,
          readXlsx: this.readXlsx,
        }),
        parameters: z.object({
          file_path: z.string().describe('要读取文件的绝对路径'),
          offset: z
            .number()
            .int()
            .min(1)
            .optional()
            .describe(
              '开始读取的行号, 仅当文件过大无法一次性读取时提供, 行号从 1 开始',
            ),
          limit: z
            .number()
            .int()
            .min(1)
            .optional()
            .describe('要读取的行数, 仅当文件过大无法一次性读取时提供'),
        }),
        execute: async (
          {
            file_path,
            offset,
            limit,
          }: {
            file_path: string;
            offset?: number;
            limit?: number;
          },
          runContext?: RunContext,
        ): Promise<string | ToolErrorResult<string>> => {
          if (this.readXlsx) {
            const ext = fileExtension(file_path);
            if (ext === 'xls') {
              return toolError(
                `无法读取文件 \`${file_path}\`：不支持旧版二进制 .xls 文件，请另存为 .xlsx 后用 Read 读取。`,
              );
            }
            if (ext === 'xlsx' || ext === 'xlsm') {
              return await this.readSpreadsheetAsCsv(session, file_path);
            }
          }
          const ext = fileExtension(file_path);
          if (this.readPdf && ext === 'pdf') {
            return await this.readPdfAsText(session, file_path, offset, limit);
          }
          if (this.readDocx && ext === 'docx') {
            return await this.readOfficeDocumentAsText(
              session,
              file_path,
              'docx',
              offset,
              limit,
            );
          }
          if (this.readPptx && ext === 'pptx') {
            return await this.readOfficeDocumentAsText(
              session,
              file_path,
              'pptx',
              offset,
              limit,
            );
          }
          try {
            // TODO: Add file_unchanged-style read cache/dedup once transcript
            // compaction and tool-result references have a stable shape here.
            const result = await withSandboxSpan(
              'sandbox.read_file',
              {
                path: file_path,
                offset,
                limit,
                run_as: this._runAs,
              },
              async () =>
                await session.readTextFile!({
                  path: file_path,
                  offset,
                  limit,
                  maxBytes: limit === undefined ? this.maxReadBytes : undefined,
                  runAs: this._runAs,
                }),
            );
            setReadState(runContext, session, this._runAs, file_path, {
              content: result.content,
              mtimeMs: result.mtimeMs,
              offset,
              limit,
            });
            return renderReadFileResult(result);
          } catch (error) {
            return fsToolError('读取文件', file_path, error);
          }
        },
      }),

      tool({
        name: 'Write',
        description: `将文件写入本地文件系统。

用法：
如果提供的路径已存在文件，此工具将覆盖现有文件
如果是现有文件，您必须首先使用 Read 工具读取该文件的内容。如果未先读取文件，此工具将操作失败
修改现有文件时，请优先使用 Edit 工具——它仅发送差异部分。仅当创建新文件或进行完全重写时才使用本工具
除非用户明确要求，否则切勿创建文档文件（*.md）或 README 文件
仅在用户明确要求时才使用表情符号。避免在文件中写入表情符号，除非被要求这样`,
        parameters: z.object({
          file_path: z
            .string()
            .describe('要写入文件的绝对路径(必须是绝对路径，不能是相对路径)'),
          content: z.string().describe('要写入的完整文件内容'),
        }),
        execute: async (
          {
            file_path,
            content,
          }: {
            file_path: string;
            content: string;
          },
          runContext?: RunContext,
        ): Promise<string | ToolErrorResult<string>> => {
          try {
            const statResult = await session.statFile!({
              path: file_path,
              runAs: this._runAs,
            });
            if (statResult.exists && statResult.isFile) {
              const blocked = await validateExistingFileWrite({
                session,
                runContext,
                runAs: this._runAs,
                path: file_path,
                statResult,
              });
              if (blocked) {
                return toolError(blocked);
              }
            }
            const result = await withSandboxSpan(
              'sandbox.write_file',
              {
                path: file_path,
                content_bytes: Buffer.byteLength(content, 'utf8'),
                run_as: this._runAs,
              },
              async () =>
                await session.writeFile!({
                  path: file_path,
                  content,
                  runAs: this._runAs,
                } satisfies WriteFileArgs),
            );
            await refreshFullReadState(
              runContext,
              session,
              this._runAs,
              file_path,
              {
                content,
              },
            );
            return result.type === 'create'
              ? `文件已成功创建：${result.path}`
              : `文件 ${result.path} 已成功更新。`;
          } catch (error) {
            return fsToolError('写入文件', file_path, error);
          }
        },
      }),
      tool({
        name: 'Edit',
        description: `在文件中执行精确的字符串替换。
用法：
- 在编辑之前，你必须在本次对话中至少使用一次 \`Read\` 工具
如果没有先读取文件就尝试编辑，此工具会报错
- 编辑来自 Read 工具输出的文本时，请确保保留行号前缀之后显示的精确缩进(tabs/spaces)。行号前缀的格式是：行号 + 制表符。其后的所有内容才是需要匹配的实际文件内容。切勿在 old_string 或 new_string 中包含行号前缀的任何部分
- 始终优先编辑代码库中的现有文件。除非明确要求，否则绝不要写入新文件
- 只有在用户明确要求时才使用 emoji。除非被要求，否则避免向文件中添加 emoji
- 如果 \`old_string\` 在文件中并不唯一，编辑会失败
- 你需要提供一个带有更多上下文的更长字符串，使其唯一；或者使用 \`replace_all\` 来修改 \`old_string\` 的每一次出现。
- 使用尽可能小但明确唯一的 old_string——通常 2 到 4 行相邻内容就足够了。不要在较少上下文即可唯一定位目标时包含 10 行以上的上下文。
- 使用 \`replace_all\` 可以在整个文件中替换和重命名字符串。例如，当你想重命名一个变量时，这个参数会很有用`,
        parameters: z.object({
          file_path: z.string().describe('要修改的文件的绝对路径'),
          old_string: z.string().describe('要被替换的文本'),
          new_string: z
            .string()
            .describe('用于替换的新文本(必须与 old_string 不同)'),
          replace_all: z
            .boolean()
            .optional()
            .describe('是否替换所有 old_string, 默认 false'),
        }),
        execute: async (
          {
            file_path,
            old_string,
            new_string,
            replace_all,
          }: {
            file_path: string;
            old_string: string;
            new_string: string;
            replace_all?: boolean;
          },
          runContext?: RunContext,
        ): Promise<string | ToolErrorResult<string>> => {
          try {
            const statResult = await session.statFile!({
              path: file_path,
              runAs: this._runAs,
            });
            if (statResult.exists && statResult.isFile) {
              const blocked = await validateExistingFileWrite({
                session,
                runContext,
                runAs: this._runAs,
                path: file_path,
                statResult,
              });
              if (blocked) {
                return toolError(blocked);
              }
            }
            const result = await withSandboxSpan(
              'sandbox.edit_file',
              {
                path: file_path,
                replace_all,
                run_as: this._runAs,
              },
              async () =>
                await session.editFile!({
                  path: file_path,
                  oldString: old_string,
                  newString: new_string,
                  replaceAll: replace_all,
                  runAs: this._runAs,
                } satisfies EditFileArgs),
            );
            await refreshFullReadState(
              runContext,
              session,
              this._runAs,
              file_path,
            );
            return result.replacements === 1
              ? `文件 ${result.path} 已成功更新。`
              : `文件 ${result.path} 已成功更新，共替换 ${result.replacements} 处。`;
          } catch (error) {
            return fsToolError('编辑文件', file_path, error);
          }
        },
      }),
      tool({
        name: 'Grep',
        description: `一个基于 ripgrep 构建的强大搜索工具
用法：
- 搜索任务始终使用 Grep, Grep 工具已经针对正确的权限和访问方式做了优化
- 支持完整的正则表达式语法（例如："log.*Error"、"function\\\\s+\\\\w+"）
- 可以使用 glob 参数过滤文件（例如："*.js"、"**/*.tsx"），也可以使用 type 参数过滤文件类型（例如："js"、"py"、"rust"）
- 输出模式："content" 显示匹配的行，"files_with_matches" 只显示文件路径（默认），"count" 显示匹配数量
- 模式语法：使用 ripgrep（不是 grep）——字面量大括号需要转义（例如在 Go 代码中查找 \`interface{}\` 时，应使用 \`interface\\\\{\\\\}\`）
- 多行匹配：默认情况下，模式只在单行内匹配。对于跨行模式，例如 \`struct \\{[\\s\\S]*?field\`，请使用 \`multiline: true\``,
        parameters: z.object({
          pattern: z.string().describe('要在文件内容中搜索的正则表达式模式'),
          path: z
            .string()
            .optional()
            .describe('要搜索的文件或目录（rg PATH）。默认值为当前工作目录'),
          glob: z
            .string()
            .optional()
            .describe('用于过滤文件的 glob，例如 "*.js" 或 "*.{ts,tsx}"。'),
          output_mode: z
            .enum(['content', 'files_with_matches', 'count'])
            .optional()
            .describe(
              '输出模式：content 显示匹配行，files_with_matches 只显示匹配文件路径，count 显示每个文件的匹配次数。默认 files_with_matches。',
            ),
          '-B': z
            .number()
            .int()
            .min(0)
            .optional()
            .describe('content 模式下，每个匹配项前显示多少行上下文'),
          '-A': z
            .number()
            .int()
            .min(0)
            .optional()
            .describe('content 模式下，每个匹配项后显示多少行上下文'),
          '-C': z
            .number()
            .int()
            .min(0)
            .optional()
            .describe('context 的别名，表示匹配项前后各显示多少行上下文'),
          context: z
            .number()
            .int()
            .min(0)
            .optional()
            .describe('content 模式下，匹配项前后各显示多少行上下文'),
          '-n': z
            .boolean()
            .optional()
            .describe('content 模式下是否显示行号。默认 true。'),
          '-i': z.boolean().optional().describe('是否忽略大小写'),
          type: z
            .string()
            .optional()
            .describe('按 ripgrep 文件类型过滤，例如 js、py、rust、go'),
          head_limit: z
            .number()
            .int()
            .min(0)
            .optional()
            .describe(
              '最多返回多少行或条目。默认 250；传 0 表示不限制，请谨慎使用',
            ),
          offset: z
            .number()
            .int()
            .min(0)
            .optional()
            .describe('跳过前 N 行或条目后再应用 head_limit。默认 0'),
          multiline: z
            .boolean()
            .optional()
            .describe('是否启用多行匹配，允许模式跨行匹配。默认 false'),
        }),
        execute: async ({
          pattern,
          path,
          glob,
          output_mode,
          '-B': beforeContext,
          '-A': afterContext,
          '-C': contextAlias,
          context,
          '-n': showLineNumbers,
          '-i': caseInsensitive,
          type,
          head_limit,
          offset,
          multiline,
        }: {
          pattern: string;
          path?: string;
          glob?: string;
          output_mode?: 'content' | 'files_with_matches' | 'count';
          '-B'?: number;
          '-A'?: number;
          '-C'?: number;
          context?: number;
          '-n'?: boolean;
          '-i'?: boolean;
          type?: string;
          head_limit?: number;
          offset?: number;
          multiline?: boolean;
        }): Promise<string | ToolErrorResult<string>> => {
          const searchPath = path ?? '.';
          try {
            const result = await withSandboxSpan(
              'sandbox.grep_files',
              {
                path: searchPath,
                output_mode,
                glob,
                type,
                run_as: this._runAs,
              },
              async () =>
                await session.grepFiles!({
                  pattern,
                  path,
                  glob,
                  outputMode: output_mode,
                  beforeContext,
                  afterContext,
                  context: context ?? contextAlias,
                  showLineNumbers,
                  caseInsensitive,
                  type,
                  headLimit: head_limit,
                  offset,
                  multiline,
                  runAs: this._runAs,
                } satisfies GrepFilesArgs),
            );
            return renderGrepResult(result);
          } catch (error) {
            return fsToolError('搜索文件', searchPath, error);
          }
        },
      }),
      tool({
        name: 'Glob',
        description:
          '快速的文件模式匹配工具，适用于任意规模的代码库\n- 支持类似 "**/*.js" 或 "src/**/*.ts" 的 glob 模式\n- 返回按修改时间排序的匹配文件路径\n- 当你需要根据文件名模式查找文件时，使用此工具\n',
        parameters: z.object({
          pattern: z.string().describe('要匹配的 glob 文件模式'),
          path: z
            .string()
            .optional()
            .describe(
              '要搜索的目录。如果未指定，则使用当前工作目录。重要：如果要使用默认目录，请省略此字段。不要填写 "undefined" 或 "null" —— 只需省略它即可使用默认行为。如果提供该字段，则必须是一个有效的目录路径',
            ),
        }),
        execute: async ({
          pattern,
          path,
        }: {
          pattern: string;
          path?: string;
        }): Promise<string | ToolErrorResult<string>> => {
          const searchPath = path ?? '.';
          try {
            const result = await withSandboxSpan(
              'sandbox.glob_files',
              {
                path: searchPath,
                pattern,
                limit: this.maxGlobResults,
                run_as: this._runAs,
              },
              async () =>
                await session.globFiles!({
                  pattern,
                  path,
                  limit: this.maxGlobResults,
                  runAs: this._runAs,
                } satisfies GlobFilesArgs),
            );
            return renderGlobResult(result);
          } catch (error) {
            return fsToolError('查找文件', searchPath, error);
          }
        },
      }),
      tool({
        name: 'LS',
        description:
          '列出工作区中某个目录的直接子项。不递归。用于查看目录结构或确认文件是否存在。',
        parameters: z.object({
          path: z
            .string()
            .optional()
            .describe('要列出的目录路径。不传则列出工作区根目录。'),
        }),
        execute: async ({
          path,
        }: {
          path?: string;
        }): Promise<string | ToolErrorResult<string>> => {
          const listPath = path ?? '.';
          try {
            const entries = await withSandboxSpan(
              'sandbox.list_dir',
              {
                path: listPath,
                run_as: this._runAs,
              },
              async () =>
                await session.listDir!({
                  path: listPath,
                  runAs: this._runAs,
                } satisfies ListDirectoryArgs),
            );
            return renderListDirectoryResult(listPath, entries);
          } catch (error) {
            return fsToolError('列出目录', listPath, error);
          }
        },
      }),
    ];

    tools.push(this.createViewImageTool(session));

    const configuredTools = this.configureTools
      ? this.configureTools([...tools])
      : tools;
    this.exposedToolNames = new Set(configuredTools.map((tool) => tool.name));
    validateFilesystemToolSessionMethods(
      session,
      this.exposedToolNames,
      this.readXlsx || this.readPdf || this.readDocx || this.readPptx,
    );
    return configuredTools;
  }

  /**
   * view_image 按主模型能力和 captioner 配置三分支生成（bindModel 在 tools()
   * 之前完成，分支在此时即可确定）：
   * - 工具结果通道能送图（native/synthetic-user）：现有原生路径，schema 无 focus。
   * - 送不了图但配置了 captioner：schema 为 { path, focus }（focus 必填），
   *   由视觉 fallback 模型生成文本描述写回。
   * - 都没有：保留 { path } schema，调用返回明确的不支持 toolError，
   *   给用户一个「UI 能传图但当前模型/配置读不了」的确定反馈。
   */
  private createViewImageTool(session: SandboxSession): Tool<any> {
    const imageMode = resolveImageToolResultMode(this._modelInstance?.metadata);
    if (imageMode !== 'unsupported') {
      return this.createNativeViewImageTool(session);
    }
    if (this.viewImageCaptioner) {
      return this.createCaptionerViewImageTool(
        session,
        this.viewImageCaptioner,
      );
    }
    return createUnsupportedViewImageTool();
  }

  private createNativeViewImageTool(session: SandboxSession): Tool<any> {
    return tool({
      name: 'view_image',
      description:
        '读取 sandbox 工作区中的图片路径，并把图片内容返回给当前支持视觉输入的模型。仅支持 PNG/JPEG/WEBP/GIF；如果图片无法安全读取，会返回文本错误。',
      parameters: z.object({
        path: z.string().describe('sandbox 工作区中的图片文件路径。'),
      }),
      execute: async ({
        path,
      }: {
        path: string;
      }): Promise<ViewImageToolResult> => {
        if (!session.viewImage) {
          return '当前 sandbox 不支持读取图片。';
        }
        const limits = resolveViewImageLimits(this._modelInstance?.metadata);
        try {
          const output = await withSandboxSpan(
            'sandbox.view_image',
            {
              path,
              run_as: this._runAs,
            },
            async () =>
              await session.viewImage!({
                path,
                runAs: this._runAs,
                maxBytes: limits.maxImageBytes,
              } satisfies ViewImageArgs),
          );
          const validationError = validateViewImageOutput(
            path,
            output,
            this._modelInstance?.metadata,
          );
          if (validationError) {
            return validationError;
          }
          const display = getViewImageDisplayMetadata(output);
          return toolResult({ output, display });
        } catch (error) {
          return renderViewImageError(path, error);
        }
      },
    });
  }

  private createCaptionerViewImageTool(
    session: SandboxSession,
    captioner: ViewImageCaptionerArgs,
  ): Tool<any> {
    return tool({
      name: 'view_image',
      description:
        '读取 sandbox 工作区中的图片路径。当前不支持原生视觉输入，图片会交给配置的视觉模型按 focus 提取信息，并以文本形式返回提取结果。返回的是二手视觉描述，可能遗漏或误读细节。仅支持 PNG/JPEG/WEBP/GIF。',
      parameters: z.object({
        path: z.string().describe('sandbox 工作区中的图片文件路径。'),
        focus: z
          .string()
          .describe(
            '你为了回答用户问题，需要视觉模型重点观察、提取、判断的信息。不要粘贴完整对话历史；只包含与本次图片理解直接相关的目标、约束和上下文。',
          ),
      }),
      execute: async (
        { path, focus }: { path: string; focus: string },
        _runContext?: RunContext,
        details?: ToolCallDetails,
      ): Promise<string | ToolDualResult<string> | ToolErrorResult<string>> => {
        if (!session.viewImage) {
          return '当前 sandbox 不支持读取图片。';
        }
        // 图片实际发给 captioner 模型，尺寸/byte 校验用它的 metadata（主模型
        // 是文本模型，multimodal 限制不存在）。
        const captionerMetadata = captioner.model.metadata;
        const limits = resolveViewImageLimits(captionerMetadata);
        let output: ToolOutputImage;
        try {
          output = await withSandboxSpan(
            'sandbox.view_image',
            {
              path,
              run_as: this._runAs,
            },
            async () =>
              await session.viewImage!({
                path,
                runAs: this._runAs,
                maxBytes: limits.maxImageBytes,
              } satisfies ViewImageArgs),
          );
        } catch (error) {
          return renderViewImageError(path, error);
        }
        const validationError = validateViewImageOutput(
          path,
          output,
          captionerMetadata,
        );
        if (validationError) {
          return validationError;
        }
        const display = getViewImageDisplayMetadata(output);
        const inlineImage = resolveViewImageInlineData(output);
        if (!inlineImage) {
          return `无法读取图片 \`${path}\`：未能获得内联图片数据。`;
        }
        try {
          const caption = await this.generateViewImageCaption({
            captioner,
            path,
            focus,
            display,
            inlineImage,
            signal: details?.signal,
          });
          return toolResult({
            output: renderViewImageCaptionOutput({ path, focus, caption }),
            display,
          });
        } catch (error) {
          return toolError(
            `图片已读取，但视觉 fallback 模型生成描述失败：${truncateCaptionerErrorMessage(toErrorMessage(error))}。当前主模型不支持原生图片理解，请用户改用文字描述图片或切换支持视觉输入的模型。`,
          );
        }
      },
    });
  }

  /**
   * 直调 captioner 模型生成图片描述（grill 定稿：不包内部 Agent、无工具、
   * 不读会话历史；只接收 focus + 图片 + 图片元数据）。
   * 用量不并入 run usage，只写进 sandbox span 属性（spanData 在 end 事件
   * 发出时重新展开，调用后回填即可生效）。
   */
  private async generateViewImageCaption(args: {
    captioner: ViewImageCaptionerArgs;
    path: string;
    focus: string;
    display: ViewImageMetadata | undefined;
    inlineImage: string;
    signal?: AbortSignal;
  }): Promise<string> {
    const { captioner, path, focus, display, inlineImage, signal } = args;
    const timeoutMs =
      captioner.timeoutMs ?? DEFAULT_VIEW_IMAGE_CAPTIONER_TIMEOUT_MS;
    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => {
      timeoutController.abort();
    }, timeoutMs);
    const { signal: combinedSignal, cleanup } = combineAbortSignals(
      signal,
      timeoutController.signal,
    );
    const spanData: Record<string, unknown> = {
      path,
      run_as: this._runAs,
      timeout_ms: timeoutMs,
    };
    try {
      return await withSandboxSpan(
        'sandbox.view_image_caption',
        spanData,
        async () => {
          const { reasoning, ...modelSettings } = captioner.modelSettings ?? {};
          let response: ModelResponse;
          try {
            response = await captioner.model.getResponse({
              systemInstructions: VIEW_IMAGE_CAPTIONER_SYSTEM_INSTRUCTIONS,
              input: [
                {
                  type: 'message',
                  role: 'user',
                  content: [
                    {
                      type: 'input_text',
                      text: renderViewImageCaptionerUserText({
                        path,
                        focus,
                        display,
                      }),
                    },
                    { type: 'input_image', image: inlineImage },
                  ],
                },
              ],
              modelSettings: {
                maxTokens: 1000,
                temperature: 0,
                toolChoice: 'none',
                reasoning: { effort: 'none', ...(reasoning ?? {}) },
                ...modelSettings,
              },
              tools: [],
              toolsExplicitlyProvided: true,
              handoffs: [],
              outputType: 'text',
              tracing: 'enabled_without_data',
              signal: combinedSignal,
            });
          } catch (error) {
            if (timeoutController.signal.aborted) {
              throw new UserError(
                `视觉 fallback 模型调用超时（超过 ${timeoutMs}ms）`,
              );
            }
            throw error;
          }
          spanData.input_tokens = response.usage?.inputTokens;
          spanData.output_tokens = response.usage?.outputTokens;
          const text = extractResponseOutputText(response);
          if (text.trim().length === 0) {
            throw new UserError('视觉 fallback 模型没有返回文本输出');
          }
          return text.trim();
        },
      );
    } finally {
      clearTimeout(timeoutId);
      cleanup();
    }
  }

  override instructions(): string {
    return renderFilesystemCapabilityInstructions(this.exposedToolNames);
  }
}
export type Filesystem = FilesystemCapability;
export function filesystem(args: FilesystemArgs = {}): Filesystem {
  return new FilesystemCapability(args);
}

function validateFilesystemToolSessionMethods(
  session: SandboxSession,
  toolNames: Set<string>,
  readsBinaryDocuments: boolean,
): void {
  if (toolNames.has('Read') && !session.readTextFile) {
    throw new UserError('Filesystem sandbox session 必须提供 readTextFile()。');
  }
  if (toolNames.has('Read') && readsBinaryDocuments) {
    if (!session.readFile) {
      throw new UserError(
        '启用二进制文档读取时，Filesystem sandbox session 必须提供 readFile()。',
      );
    }
    if (!session.writeFile) {
      throw new UserError(
        '启用二进制文档读取时（用于导出超大文档），Filesystem sandbox session 必须提供 writeFile()。',
      );
    }
  }
  if (
    (toolNames.has('Read') ||
      toolNames.has('Write') ||
      toolNames.has('Edit')) &&
    !session.statFile
  ) {
    throw new UserError('Filesystem sandbox session 必须提供 statFile()。');
  }
  if (toolNames.has('Write') && !session.writeFile) {
    throw new UserError('Filesystem sandbox session 必须提供 writeFile()。');
  }
  if (toolNames.has('Edit') && !session.editFile) {
    throw new UserError('Filesystem sandbox session 必须提供 editFile()。');
  }
  if (toolNames.has('Grep') && !session.grepFiles) {
    throw new UserError('Filesystem sandbox session 必须提供 grepFiles()。');
  }
  if (toolNames.has('Glob') && !session.globFiles) {
    throw new UserError('Filesystem sandbox session 必须提供 globFiles()。');
  }
  if (toolNames.has('LS') && !session.listDir) {
    throw new UserError('Filesystem sandbox session 必须提供 listDir()。');
  }
  if (toolNames.has('view_image') && !session.viewImage) {
    throw new UserError('Filesystem sandbox session 必须提供 viewImage()。');
  }
}

type ImageToolResultMode = 'native' | 'synthetic-user' | 'unsupported';

const VIEW_IMAGE_SUPPORTED_MEDIA_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
]);

function resolveImageToolResultMode(
  metadata: ModelMetadata | undefined,
): ImageToolResultMode {
  const multimodal = metadata?.multimodal;
  if (!multimodal?.imageInput) {
    return 'unsupported';
  }
  if (multimodal.imageToolResult === 'native') {
    return 'native';
  }
  if (multimodal.imageToolResult === 'synthetic-user') {
    return 'synthetic-user';
  }
  return 'unsupported';
}

/**
 * 文本主模型 + 未配置 captioner：保留 { path } schema，不读图片 bytes，
 * 直接返回不支持 toolError（status 进 incomplete，run 不崩）。
 */
function createUnsupportedViewImageTool(): Tool<any> {
  return tool({
    name: 'view_image',
    description:
      '读取 sandbox 工作区中的图片路径。注意：当前模型不支持原生图片理解，且本次运行未配置视觉 fallback 模型；调用本工具只会得到明确的不支持说明。请优先请用户用文字描述图片内容。',
    parameters: z.object({
      path: z.string().describe('sandbox 工作区中的图片文件路径。'),
    }),
    execute: async (): Promise<ToolErrorResult<string>> =>
      toolError(
        '当前模型不支持原生图片理解，且本次运行未配置视觉 fallback 模型。请切换到支持视觉输入的模型，或请用户用文字描述图片内容。',
      ),
  });
}

const VIEW_IMAGE_CAPTIONER_SYSTEM_INSTRUCTIONS = `你是一个图片信息提取器，不是最终回答助手。

你的任务是根据提供的 focus，从图片中提取对该 focus 有帮助的视觉信息，供另一个文本模型继续回答用户。

只基于图片可见内容回答。不要补充外部背景知识、资料解释或常识推断。
不要直接回答用户，不要解题，不要给最终建议，除非某个结论可以直接从图片可见内容判断并且与 focus 有关。

把直接观察、与 focus 相关的结论、不确定或无法判断的内容分开写。
OCR、公式、图表读数可以尝试，但必须标注看不清或不确定的部分。
如果图片内容与 focus 无关，请简短说明无关。`;

function renderViewImageCaptionerUserText(args: {
  path: string;
  focus: string;
  display: ViewImageMetadata | undefined;
}): string {
  const { path, focus, display } = args;
  return [
    `path: ${path}`,
    ...(display
      ? [
          `mimeType: ${display.mimeType}`,
          `dimensions: ${display.width}x${display.height}`,
          `byteSize: ${display.byteSize}`,
        ]
      : []),
    '',
    'focus:',
    focus,
    '',
    '请按以下结构输出：',
    '',
    '关键观察:',
    '- ...',
    '',
    '与 focus 直接相关的结论:',
    '- ...',
    '',
    '可见文字/OCR:',
    '- ...',
    '',
    '数值/坐标/图表读数:',
    '- ...',
    '',
    '无法可靠判断:',
    '- ...',
  ].join('\n');
}

/**
 * caption 是普通文本，可持久化进模型历史（无 base64 风险）；开头固定
 * 免责说明，避免主模型把二手视觉描述当成原生视觉事实。
 */
function renderViewImageCaptionOutput(args: {
  path: string;
  focus: string;
  caption: string;
}): string {
  return [
    '以下是视觉 fallback 模型根据 focus 生成的图片信息提取结果；它可能遗漏或误读细节。',
    '',
    `path: ${args.path}`,
    `focus: ${args.focus}`,
    '',
    args.caption,
  ].join('\n');
}

/** 把 session.viewImage 的输出转成 input_image 可用的内联数据（data URL / URL）。 */
function resolveViewImageInlineData(
  output: ToolOutputImage,
): string | undefined {
  const image = output.image;
  if (typeof image === 'string') {
    return image;
  }
  if (!image || typeof image !== 'object') {
    return undefined;
  }
  if ('data' in image) {
    const mediaType =
      image.mediaType ?? getViewImageProviderData(output)?.mimeType;
    if (!mediaType) {
      return undefined;
    }
    const base64 =
      typeof image.data === 'string'
        ? image.data
        : Buffer.from(image.data).toString('base64');
    return `data:${mediaType};base64,${base64}`;
  }
  if ('url' in image) {
    return image.url;
  }
  return undefined;
}

function extractResponseOutputText(response: ModelResponse): string {
  return response.output
    .flatMap((item) => {
      if (!item || typeof item !== 'object' || item.type !== 'message') {
        return [];
      }
      if (typeof item.content === 'string') {
        return [item.content];
      }
      return item.content.flatMap((part) => {
        if (!part || typeof part !== 'object') {
          return [];
        }
        return part.type === 'output_text' ? [part.text] : [];
      });
    })
    .join('');
}

function truncateCaptionerErrorMessage(message: string): string {
  const normalized = message.replace(/\s+/gu, ' ').trim();
  return normalized.length <= VIEW_IMAGE_CAPTIONER_ERROR_MESSAGE_MAX_CHARS
    ? normalized
    : `${normalized.slice(0, VIEW_IMAGE_CAPTIONER_ERROR_MESSAGE_MAX_CHARS)}…`;
}

function validateViewImageOutput(
  path: string,
  output: ToolOutputImage,
  metadata: ModelMetadata | undefined,
): string | null {
  const providerData = getViewImageProviderData(output);
  if (!providerData) {
    return `无法读取图片 \`${path}\`：未能识别图片元数据。`;
  }
  if (!VIEW_IMAGE_SUPPORTED_MEDIA_TYPES.has(providerData.mimeType)) {
    return `图片路径 \`${path}\` 不是支持的图片格式。当前仅支持 PNG、JPEG、WEBP、GIF。`;
  }
  const display = getViewImageDisplayMetadata(output);
  if (!display) {
    return `无法读取图片 \`${path}\`：无法嗅探图片尺寸。`;
  }
  if (
    !Number.isFinite(display.width) ||
    !Number.isFinite(display.height) ||
    !Number.isFinite(display.pixelCount)
  ) {
    return `无法读取图片 \`${path}\`：无法嗅探图片尺寸。`;
  }

  const limits = resolveViewImageLimits(metadata);
  if (display.width > limits.maxImageWidth) {
    return `图片尺寸超过当前模型限制：${path} 宽度为 ${display.width}，上限为 ${limits.maxImageWidth}。请缩小后再试。`;
  }
  if (display.height > limits.maxImageHeight) {
    return `图片尺寸超过当前模型限制：${path} 高度为 ${display.height}，上限为 ${limits.maxImageHeight}。请缩小后再试。`;
  }
  if (display.pixelCount > limits.maxImagePixels) {
    return `图片尺寸超过当前模型限制：${path} 为 ${display.width}x${display.height} (${display.pixelCount} pixels)，请缩小后再试。`;
  }
  if (display.byteSize > limits.maxImageBytes) {
    return `图片文件超过当前模型传输限制：${path} 为 ${display.byteSize} 字节，上限为 ${limits.maxImageBytes} 字节。请压缩或缩小后再试。`;
  }

  return null;
}

function resolveViewImageLimits(
  metadata: ModelMetadata | undefined,
): ViewImageLimits {
  const multimodal = metadata?.multimodal;
  return {
    maxImageWidth: positiveLimit(
      multimodal?.maxImageWidth,
      DEFAULT_VIEW_IMAGE_MAX_WIDTH,
    ),
    maxImageHeight: positiveLimit(
      multimodal?.maxImageHeight,
      DEFAULT_VIEW_IMAGE_MAX_HEIGHT,
    ),
    maxImagePixels: positiveLimit(
      multimodal?.maxImagePixels,
      DEFAULT_VIEW_IMAGE_MAX_PIXELS,
    ),
    maxImageBytes: positiveLimit(
      multimodal?.maxImageBytes,
      DEFAULT_VIEW_IMAGE_MAX_BYTES,
    ),
  };
}

function positiveLimit(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : fallback;
}

function getViewImageDisplayMetadata(
  output: ToolOutputImage,
): ViewImageMetadata | undefined {
  const providerData = getViewImageProviderData(output);
  if (!isViewImageMetadata(providerData)) {
    return undefined;
  }
  return {
    kind: 'view_image',
    path: providerData.path,
    mimeType: providerData.mimeType,
    byteSize: providerData.byteSize,
    width: providerData.width,
    height: providerData.height,
    pixelCount: providerData.pixelCount,
    estimatedImageTokens: providerData.estimatedImageTokens,
  };
}

function getViewImageProviderData(
  output: ToolOutputImage,
):
  | { kind: 'view_image'; path: string; mimeType: string; byteSize: number }
  | undefined {
  const providerData = output.providerData;
  if (!providerData || typeof providerData !== 'object') {
    return undefined;
  }
  const record = providerData as Record<string, unknown>;
  if (
    record.kind !== 'view_image' ||
    typeof record.path !== 'string' ||
    typeof record.mimeType !== 'string' ||
    typeof record.byteSize !== 'number'
  ) {
    return undefined;
  }
  return {
    kind: 'view_image',
    path: record.path,
    mimeType: record.mimeType,
    byteSize: record.byteSize,
    ...(typeof record.width === 'number' ? { width: record.width } : {}),
    ...(typeof record.height === 'number' ? { height: record.height } : {}),
    ...(typeof record.pixelCount === 'number'
      ? { pixelCount: record.pixelCount }
      : {}),
    ...(typeof record.estimatedImageTokens === 'number'
      ? { estimatedImageTokens: record.estimatedImageTokens }
      : {}),
  };
}

function isViewImageMetadata(value: unknown): value is ViewImageMetadata {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    record.kind === 'view_image' &&
    typeof record.path === 'string' &&
    typeof record.mimeType === 'string' &&
    typeof record.byteSize === 'number' &&
    typeof record.width === 'number' &&
    typeof record.height === 'number' &&
    typeof record.pixelCount === 'number' &&
    typeof record.estimatedImageTokens === 'number'
  );
}

/**
 * Read 工具描述按启用的二进制文档抽取能力动态拼装：
 * 未启用的格式不出现在描述里，避免向模型承诺不存在的能力。
 */
function renderReadToolDescription(formats: {
  readPdf: boolean;
  readDocx: boolean;
  readPptx: boolean;
  readXlsx: boolean;
}): string {
  const formatLines = [
    formats.readPdf
      ? '- PDF 文件仅支持有文本层的内容；扫描件或图片型 PDF 暂不支持 OCR'
      : null,
    formats.readDocx
      ? '- docx 文件会抽取可读文本；图片、扫描件、嵌入对象中的文字暂不支持 OCR 或对象解析'
      : null,
    formats.readPptx
      ? '- pptx 文件会抽取 slide 正文与 notes 文本；母版、批注、图片、图表中的文字暂不支持解析'
      : null,
    formats.readXlsx
      ? '- xlsx/xlsm 表格会解析为 CSV 文本返回；旧版二进制 .xls 不支持，需先另存为 .xlsx'
      : null,
  ].filter((line) => line !== null);
  return [
    '从本地文件系统读取文件',
    '如果用户提供了某个文件路径，请假定该路径是有效的。尝试读取一个不存在的文件也是可以的；届时会返回错误',
    '用法:',
    '- file_path 参数必须是绝对路径，而不是相对路径',
    '- 默认情况下，它会从文件开头开始读取最多 2000 行',
    '- 你可以选择指定行偏移量和读取行数限制(对于长文件尤其方便)，但建议不提供这些参数，直接读取整个文件',
    '- 结果会以 cat -n 格式返回，行号从 1 开始',
    ...formatLines,
    ...(formatLines.length > 0
      ? ['- 此工具只能读取文本类文件，不能读取目录与二进制文件']
      : []),
    '- 如果你读取的文件存在但内容为空，你会收到一条系统提醒警告，而不是文件内容',
  ].join('\n');
}

function renderFilesystemCapabilityInstructions(
  toolNames = DEFAULT_FILESYSTEM_TOOL_NAMES,
): string {
  const hasRead = toolNames.has('Read');
  const hasWrite = toolNames.has('Write');
  const hasEdit = toolNames.has('Edit');
  const hasGrep = toolNames.has('Grep');
  const hasGlob = toolNames.has('Glob');
  const hasLS = toolNames.has('LS');
  const hasViewImage = toolNames.has('view_image');
  const providedToolSubitems = [
    hasRead ? `读取文件时，请使用 Read` : null,
    hasEdit ? `编辑文件时，请使用 Edit` : null,
    hasWrite ? `创建文件时，请使用 Write` : null,
    hasGlob ? `搜索文件时，请使用 Glob` : null,
    hasGrep ? `搜索文件内容时，请使用 Grep` : null,
    hasLS ? `查看目录内容时，请使用 LS 查看目录的直接子项` : null,
    hasViewImage ? `查看图片内容时，请使用 view_image 按需读取图片` : null,
  ].filter(Boolean) as string[];

  const items = [
    ...providedToolSubitems,
    // taskToolName
    //   ? `使用 ${taskToolName} 工具拆分并管理你的工作。这些工具有助于规划工作，并帮助用户跟踪你的进展。每完成一项任务，就立即将其标记为已完成。不要等多项任务都完成后再批量标记。`
    //   : null,
    `你可以在单次响应中调用多个工具。如果你打算调用多个工具，并且这些调用之间没有依赖关系，请并行发起所有相互独立的工具调用。尽可能最大化使用并行工具调用，以提高效率。但是，如果某些工具调用依赖于先前调用的结果来确定后续参数，就不要并行调用这些工具，而应按顺序依次调用。例如，如果某个操作必须在另一个操作开始前完成，就应按顺序执行这些操作。`,
  ].filter((item) => item !== null);
  const useTools = [...prependBullets(items)].join(`\n`);

  return useTools;
}

function getReadStateStore(
  runContext: RunContext | undefined,
): FilesystemReadStateStore {
  if (!runContext) {
    return new Map();
  }
  return runContext._getEphemeralSharedState(
    FILESYSTEM_READ_STATE_KEY,
    () => new Map<string, FilesystemReadState>(),
  );
}

function setReadState(
  runContext: RunContext | undefined,
  session: SandboxSession,
  runAs: string | undefined,
  path: string,
  state: FilesystemReadState,
): void {
  getReadStateStore(runContext).set(readStateKey(session, runAs, path), state);
}

async function validateExistingFileWrite(args: {
  session: SandboxSession;
  runContext: RunContext | undefined;
  runAs: string | undefined;
  path: string;
  statResult: StatFileResult;
}): Promise<string | null> {
  const { session, runContext, runAs, path, statResult } = args;
  const state = getReadStateStore(runContext).get(
    readStateKey(session, runAs, path),
  );
  if (!state || state.isPartialView) {
    return '文件尚未读取。写入或编辑前，请先使用 Read 读取该文件。';
  }
  if (statResult.mtimeMs <= state.mtimeMs) {
    return null;
  }

  const isFullRead = state.offset === undefined && state.limit === undefined;
  if (isFullRead) {
    const current = await session.readTextFile!({
      path,
      runAs,
    });
    if (current.content === state.content) {
      return null;
    }
  }

  return '文件在读取后已被用户、格式化工具或其他进程修改。请重新使用 Read 读取后再写入或编辑。';
}

async function refreshFullReadState(
  runContext: RunContext | undefined,
  session: SandboxSession,
  runAs: string | undefined,
  path: string,
  known?: { content: string },
): Promise<void> {
  const statResult = await session.statFile!({
    path,
    runAs,
  });
  if (known) {
    setReadState(runContext, session, runAs, path, {
      content: known.content,
      mtimeMs: statResult.mtimeMs,
    });
    return;
  }

  const current = await session.readTextFile!({
    path,
    runAs,
  });
  setReadState(runContext, session, runAs, path, {
    content: current.content,
    mtimeMs: current.mtimeMs,
  });
}

function readStateKey(
  session: SandboxSession,
  runAs: string | undefined,
  path: string,
): string {
  return `${sessionReadStateId(session)}\0${runAs ?? ''}\0${normalizeReadStatePath(path)}`;
}

function sessionReadStateId(session: SandboxSession): number {
  const existing = SESSION_READ_STATE_IDS.get(session);
  if (existing !== undefined) {
    return existing;
  }
  const next = nextSessionReadStateId++;
  SESSION_READ_STATE_IDS.set(session, next);
  return next;
}

function normalizeReadStatePath(path: string): string {
  const normalized = pathPosix.normalize(path.replace(/\\/gu, '/'));
  return normalized === '.' ? '' : normalized;
}

function renderViewImageError(path: string, error: unknown): string {
  const message = toErrorMessage(error);
  if (/not found|不存在|找不到/iu.test(message)) {
    return `图片路径 \`${path}\` 不存在`;
  }
  if (/not a file|不是文件/iu.test(message)) {
    return `图片路径 \`${path}\` 不是文件`;
  }
  if (
    /unsupported image format|not a supported image|不支持的图片格式|不是支持的图片/iu.test(
      message,
    )
  ) {
    return `图片路径 \`${path}\` 不是支持的图片文件`;
  }

  return `无法读取图片 \`${path}\`：${message}`;
}

function toErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message || error.toString();
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function renderReadFileResult(result: ReadTextFileResult): string {
  // TODO: Add PDF and notebook handling when this project has a transport
  // story for document/image blocks across OpenAI-compatible models.
  if (result.totalLines === 0) {
    return '<system-reminder>警告：文件存在，但内容为空。</system-reminder>';
  }

  if (result.startLine > result.totalLines) {
    return `<system-reminder>警告：文件存在，但行数少于给定的 offset (${result.startLine})。该文件共有 ${result.totalLines} 行。</system-reminder>`;
  }

  const selected =
    result.content.length === 0 ? [] : result.content.split(/\n/u);
  const rendered = selected
    .map(
      (line, index) =>
        `${formatReadLineNumber(result.startLine + index)}\t${line}`,
    )
    .join('\n');
  return rendered;
}

function renderGrepResult(result: GrepFilesResult): string {
  const limitInfo = formatGrepLimitInfo(
    result.appliedLimit,
    result.appliedOffset,
  );
  if (result.mode === 'content') {
    const content = result.content || '没有找到匹配项';
    return limitInfo ? `${content}\n\n[分页显示结果：${limitInfo}]` : content;
  }

  if (result.mode === 'count') {
    const content = result.content || '没有找到匹配项';
    const summary =
      `共找到 ${result.numMatches ?? 0} 处匹配，分布在 ${result.numFiles} 个文件中。` +
      (limitInfo ? ` 分页：${limitInfo}` : '');
    return `${content}\n\n${summary}`;
  }

  if (result.numFiles === 0) {
    return '未找到文件';
  }
  return [
    `找到 ${result.numFiles} 个文件${limitInfo ? ` ${limitInfo}` : ''}`,
    ...result.filenames,
  ].join('\n');
}

function renderGlobResult(result: GlobFilesResult): string {
  if (result.numFiles === 0) {
    return '未找到文件';
  }
  return [
    ...result.filenames,
    ...(result.truncated
      ? ['(结果已截断。请使用更具体的 path 或 pattern。)']
      : []),
  ].join('\n');
}

function renderListDirectoryResult(
  _path: string,
  entries: SandboxDirectoryEntry[],
): string {
  if (entries.length === 0) {
    return '<empty>';
  }

  const sorted = [...entries].sort(compareDirectoryEntries);
  return sorted.map(formatDirectoryEntry).join('\n');
}

function compareDirectoryEntries(
  left: SandboxDirectoryEntry,
  right: SandboxDirectoryEntry,
): number {
  return formatDirectoryEntry(left).localeCompare(formatDirectoryEntry(right));
}

function formatDirectoryEntry(entry: SandboxDirectoryEntry): string {
  if (entry.type === 'dir') {
    return `${entry.path}/`;
  }
  return entry.path;
}

function formatGrepLimitInfo(
  appliedLimit: number | undefined,
  appliedOffset: number | undefined,
): string {
  const parts: string[] = [];
  if (appliedLimit !== undefined) {
    parts.push(`limit: ${appliedLimit}`);
  }
  if (appliedOffset) {
    parts.push(`offset: ${appliedOffset}`);
  }
  return parts.join(', ');
}

function formatReadLineNumber(lineNumber: number): string {
  const text = String(lineNumber);
  return text.length >= 6 ? text : text.padStart(6, ' ');
}

function renderFilesystemError(
  action: string,
  path: string,
  error: unknown,
): string {
  const message = toErrorMessage(error);
  return `无法${action} \`${path}\`：${message}`;
}

function fsToolError(
  action: string,
  path: string,
  error: unknown,
): ToolErrorResult<string> {
  return toolError(renderFilesystemError(action, path, error));
}

const SPREADSHEET_PREVIEW_LINES = 50;
const PDF_PREVIEW_LINES = 80;
const DOCUMENT_PREVIEW_LINES = 80;

type WorkbookCsv = {
  content: string;
  sheetNames: string[];
  totalLines: number;
};

type PptxSlideEntry = {
  path: string;
  notesPath?: string;
};

type XmlOrderedNode = Record<string, unknown>;

const ORDERED_XML_PARSER = new XMLParser({
  ignoreAttributes: false,
  preserveOrder: true,
  attributeNamePrefix: '@_',
  parseAttributeValue: false,
  parseTagValue: false,
  trimValues: false,
});

const DOCX_TURNDOWN = createDocxTurndown();

/** 返回小写扩展名（不含点），无扩展名时返回空字符串。 */
function fileExtension(path: string): string {
  const base = pathPosix.basename(path.replace(/\\/gu, '/'));
  const dot = base.lastIndexOf('.');
  return dot <= 0 ? '' : base.slice(dot + 1).toLowerCase();
}

async function renderWorkbookCsv(bytes: Buffer): Promise<WorkbookCsv> {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(bytes);
  const blocks: string[] = [];
  const sheetNames: string[] = [];
  for (const sheet of workbook.worksheets) {
    sheetNames.push(sheet.name);
    const lines: string[] = [`===SHEET: ${sheet.name}===`];
    const colCount = sheet.columnCount;
    const rowCount = sheet.rowCount;
    for (let r = 1; r <= rowCount; r += 1) {
      const row = sheet.getRow(r);
      const cells: string[] = [];
      for (let c = 1; c <= colCount; c += 1) {
        cells.push(csvEscapeField(formatCellValue(row.getCell(c).value)));
      }
      lines.push(cells.join(','));
    }
    blocks.push(lines.join('\n'));
  }
  const content = blocks.join('\n\n');
  return {
    content,
    sheetNames,
    totalLines: content.length === 0 ? 0 : content.split('\n').length,
  };
}

function formatCellValue(value: ExcelJS.CellValue): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'TRUE' : 'FALSE';
  }
  if (value instanceof Date) {
    return formatSpreadsheetDate(value);
  }
  if (typeof value === 'object') {
    const record = value as unknown as Record<string, unknown>;
    if ('formula' in record || 'sharedFormula' in record) {
      if (record.result !== undefined && record.result !== null) {
        return formatCellValue(record.result as ExcelJS.CellValue);
      }
      const formula = (record.formula ?? record.sharedFormula) as
        | string
        | undefined;
      return formula ? `=${formula}` : '';
    }
    if ('error' in record && record.error) {
      return String(record.error);
    }
    if ('richText' in record && Array.isArray(record.richText)) {
      return (record.richText as Array<{ text?: string }>)
        .map((part) => part.text ?? '')
        .join('');
    }
    if ('text' in record && typeof record.text === 'string') {
      return record.text;
    }
    if ('hyperlink' in record && typeof record.hyperlink === 'string') {
      return record.hyperlink;
    }
  }
  return String(value);
}

/** 纯日期渲染为 YYYY-MM-DD，带时间则渲染完整 ISO。 */
function formatSpreadsheetDate(value: Date): string {
  const iso = value.toISOString();
  return iso.endsWith('T00:00:00.000Z') ? iso.slice(0, 10) : iso;
}

function csvEscapeField(field: string): string {
  if (/[",\n\r]/u.test(field)) {
    return `"${field.replace(/"/gu, '""')}"`;
  }
  return field;
}

function buildSpreadsheetSpillPath(
  sourcePath: string,
  mtimeMs: number,
): string {
  const base = pathPosix
    .basename(sourcePath.replace(/\\/gu, '/'))
    .replace(/\.[^.]+$/u, '');
  const safeBase = base.replace(/[^\w.-]+/gu, '_') || 'sheet';
  const hash = createHash('sha1')
    .update(`${sourcePath}\0${mtimeMs}`)
    .digest('hex')
    .slice(0, 8);
  return `.tmp/sheets/${safeBase}.${hash}.csv`;
}

function renderSpreadsheetSpillResult(
  spillPath: string,
  csv: WorkbookCsv,
): string {
  const previewLines = csv.content
    .split('\n')
    .slice(0, SPREADSHEET_PREVIEW_LINES);
  const preview = previewLines.join('\n');
  const sheets = csv.sheetNames.length > 0 ? csv.sheetNames.join('/') : '无';
  return `${preview}\n\n<system-reminder>xlsx 内容较大，已导出为 CSV：${spillPath}（共 ${csv.sheetNames.length} 张表：${sheets}，${csv.totalLines} 行）。上方为前 ${previewLines.length} 行预览；如需完整内容请用 Read 配合 offset/limit，或用 Grep 搜索该路径。</system-reminder>`;
}

function buildDocumentSpillPath(
  sourcePath: string,
  mtimeMs: number,
  ext: string,
): string {
  const base = pathPosix
    .basename(sourcePath.replace(/\\/gu, '/'))
    .replace(/\.[^.]+$/u, '');
  const safeBase = base.replace(/[^\w.-]+/gu, '_') || 'document';
  const hash = createHash('sha1')
    .update(`${sourcePath}\0${mtimeMs}`)
    .digest('hex')
    .slice(0, 8);
  return `.tmp/documents/${safeBase}.${hash}.${ext}`;
}

function renderPdfSpillResult(spillPath: string, text: string): string {
  const result = readTextRangeFromStringForTool(spillPath, text, {
    limit: PDF_PREVIEW_LINES,
    mtimeMs: 0,
  });
  const preview = renderReadFileResult(result);
  return `${preview}\n\n<system-reminder>PDF 内容较大，已导出为文本：${spillPath}（共 ${result.totalLines} 行）。上方为前 ${result.lineCount} 行预览；如需完整内容请用 Read 配合 offset/limit，或用 Grep 搜索该路径。</system-reminder>`;
}

function renderDocumentSpillResult(
  label: string,
  spillPath: string,
  text: string,
  warnings: string[] | undefined,
): string {
  const result = readTextRangeFromStringForTool(spillPath, text, {
    limit: DOCUMENT_PREVIEW_LINES,
    mtimeMs: 0,
  });
  const preview = renderReadFileResult(result);
  return appendExtractionWarnings(
    `${preview}\n\n<system-reminder>${label} 内容较大，已导出为文本：${spillPath}（共 ${result.totalLines} 行）。上方为前 ${result.lineCount} 行预览；如需完整内容请用 Read 配合 offset/limit，或用 Grep 搜索该路径。</system-reminder>`,
    label,
    warnings,
  );
}

function appendExtractionWarnings(
  text: string,
  label: string,
  warnings: string[] | undefined,
): string {
  const normalized = normalizeWarningMessages(warnings);
  if (normalized.length === 0) {
    return text;
  }
  return `${text}\n\n<system-reminder>${label} 文本抽取提示：${normalized.join('；')}</system-reminder>`;
}

function normalizeWarningMessages(warnings: string[] | undefined): string[] {
  if (!warnings) {
    return [];
  }
  return [...new Set(warnings.map((w) => w.trim()).filter(Boolean))].slice(
    0,
    5,
  );
}

function emptyOfficeDocumentReminder(format: OfficeDocumentFormat): string {
  if (format === 'docx') {
    return '<system-reminder>警告：DOCX 没有检测到可抽取文本。图片、扫描件或嵌入对象中的文字暂不支持 OCR。</system-reminder>';
  }
  return '<system-reminder>警告：PPTX 没有检测到可抽取文本。图片、扫描件、嵌入媒体或复杂图表中的文字暂不支持 OCR 或对象解析。</system-reminder>';
}

function readTextRangeFromStringForTool(
  path: string,
  raw: string,
  args: {
    offset?: number;
    limit?: number;
    mtimeMs: number;
  },
): ReadTextFileResult {
  const text = normalizeTextForRead(raw);
  const lines = splitReadLines(text);
  const startLine = args.offset ?? 1;
  const selected = selectReadLines(lines, startLine, args.limit);
  const content = selected.join('\n');
  return {
    path,
    content,
    startLine,
    lineCount: selected.length,
    totalLines: lines.length,
    totalBytes: Buffer.byteLength(text, 'utf8'),
    readBytes: Buffer.byteLength(content, 'utf8'),
    mtimeMs: args.mtimeMs,
  };
}

function normalizeTextForRead(raw: string): string {
  const withoutBom = raw.charCodeAt(0) === 0xfeff ? raw.slice(1) : raw;
  return withoutBom.replace(/\r\n/gu, '\n').replace(/\r/gu, '\n');
}

function normalizeExtractedDocumentText(raw: string): string {
  return normalizeTextForRead(raw)
    .split('\n')
    .map((line) => line.replace(/[ \t]+$/gu, ''))
    .join('\n')
    .replace(/\n{3,}/gu, '\n\n')
    .trim();
}

function splitReadLines(text: string): string[] {
  if (text.length === 0) {
    return [];
  }
  const lines = text.split('\n');
  if (text.endsWith('\n')) {
    lines.pop();
  }
  return lines;
}

function selectReadLines(
  lines: string[],
  startLine: number,
  limit: number | undefined,
): string[] {
  const startIndex = Math.max(0, startLine - 1);
  const endIndex =
    limit === undefined
      ? lines.length
      : Math.min(startIndex + limit, lines.length);
  return lines.slice(startIndex, endIndex);
}

async function extractDocxText({
  bytes,
}: {
  bytes: Buffer;
}): Promise<ExtractedDocumentText> {
  try {
    const result = await mammoth.convertToHtml(
      { buffer: bytes },
      {
        externalFileAccess: false,
        convertImage: mammoth.images.imgElement(async () => ({ src: '' })),
      },
    );
    const markdown = htmlToDocumentMarkdown(result.value);
    return {
      text: markdown,
      warnings: result.messages.map((message) => message.message),
    };
  } catch (error) {
    throw new UserError(`DOCX 文本抽取失败：${toErrorMessage(error)}`);
  }
}

function htmlToDocumentMarkdown(html: string): string {
  return normalizeExtractedDocumentText(DOCX_TURNDOWN.turndown(html));
}

function createDocxTurndown(): TurndownService {
  const service = new TurndownService({
    headingStyle: 'atx',
    bulletListMarker: '-',
    codeBlockStyle: 'fenced',
  });
  service.use(gfm);
  service.remove(['img', 'script', 'style']);
  service.addRule('allTables', {
    filter: (node) => node.nodeName === 'TABLE',
    replacement: (_content, node) => renderHtmlTableAsMarkdown(node),
  });
  return service;
}

function renderHtmlTableAsMarkdown(node: { rows?: unknown }): string {
  const table = node as {
    rows?: ArrayLike<{
      cells?: ArrayLike<{ textContent?: string | null }>;
    }>;
  };
  const rows = Array.from(table.rows ?? [])
    .map((row) =>
      Array.from(row.cells ?? []).map((cell) =>
        normalizeTableCellText(cell.textContent ?? ''),
      ),
    )
    .filter((row) => row.some((cell) => cell.length > 0));
  if (rows.length === 0) {
    return '';
  }
  const width = Math.max(...rows.map((row) => row.length));
  const padded = rows.map((row) => [
    ...row,
    ...Array.from({ length: width - row.length }, () => ''),
  ]);
  const renderedRows = padded.map(renderMarkdownTableRow);
  const separator = renderMarkdownTableRow(
    Array.from({ length: width }, () => '---'),
  );
  return `\n\n${[renderedRows[0], separator, ...renderedRows.slice(1)].join('\n')}\n\n`;
}

function renderMarkdownTableRow(cells: string[]): string {
  return `| ${cells.map(escapeMarkdownTableCell).join(' | ')} |`;
}

function normalizeTableCellText(text: string): string {
  return text.replace(/\s+/gu, ' ').trim();
}

function escapeMarkdownTableCell(text: string): string {
  return text.replace(/\\/gu, '\\\\').replace(/\|/gu, '\\|');
}

async function extractPptxText({
  bytes,
}: {
  bytes: Buffer;
}): Promise<ExtractedDocumentText> {
  try {
    const zip = await JSZip.loadAsync(bytes);
    const slides = await listPptxSlides(zip);
    const blocks: string[] = [];
    for (let index = 0; index < slides.length; index += 1) {
      const slide = slides[index];
      const slideXml = await zip.file(slide.path)?.async('text');
      if (!slideXml) {
        continue;
      }
      const slideText = extractPptxTextFromXml(slideXml);
      const notesText = slide.notesPath
        ? extractPptxTextFromXml(
            (await zip.file(slide.notesPath)?.async('text')) ?? '',
          )
        : '';
      if (!slideText.trim() && !notesText.trim()) {
        continue;
      }
      const block = [`=== Slide ${index + 1} ===`];
      if (slideText.trim()) {
        block.push(slideText);
      }
      if (notesText.trim()) {
        block.push('', `=== Notes ${index + 1} ===`, notesText);
      }
      blocks.push(block.join('\n').trimEnd());
    }
    return { text: blocks.join('\n\n') };
  } catch (error) {
    throw new UserError(`PPTX 文本抽取失败：${toErrorMessage(error)}`);
  }
}

async function listPptxSlides(zip: JSZip): Promise<PptxSlideEntry[]> {
  const ordered = await listPptxSlidesFromPresentation(zip);
  const slides = ordered.length > 0 ? ordered : listPptxSlidesByFilename(zip);
  return await Promise.all(
    slides.map(async (path) => ({
      path,
      notesPath: await resolvePptxNotesPath(zip, path),
    })),
  );
}

async function listPptxSlidesFromPresentation(zip: JSZip): Promise<string[]> {
  const presentationXml = await zip.file('ppt/presentation.xml')?.async('text');
  const relsXml = await zip
    .file('ppt/_rels/presentation.xml.rels')
    ?.async('text');
  if (!presentationXml || !relsXml) {
    return [];
  }

  const relIds = collectAttributesByLocalName(
    parseOrderedXml(presentationXml),
    'sldId',
    '@_r:id',
  );
  const relationships = collectRelationshipTargets(relsXml);
  return relIds
    .map((id) => relationships.get(id))
    .filter((target): target is string => target !== undefined)
    .map((target) => normalizePptxTarget('ppt/presentation.xml', target))
    .filter((path) => /^ppt\/slides\/slide\d+\.xml$/u.test(path));
}

function listPptxSlidesByFilename(zip: JSZip): string[] {
  return Object.keys(zip.files)
    .filter((path) => /^ppt\/slides\/slide\d+\.xml$/u.test(path))
    .sort(comparePptxNumberedPaths);
}

async function resolvePptxNotesPath(
  zip: JSZip,
  slidePath: string,
): Promise<string | undefined> {
  const relsPath = `${pathPosix.dirname(slidePath)}/_rels/${pathPosix.basename(
    slidePath,
  )}.rels`;
  const relsXml = await zip.file(relsPath)?.async('text');
  if (!relsXml) {
    return undefined;
  }
  const nodes = collectRelationshipNodes(relsXml);
  for (const node of nodes) {
    const type = node.type ?? '';
    const target = node.target ?? '';
    if (type.endsWith('/notesSlide') && target) {
      const normalized = normalizePptxTarget(slidePath, target);
      if (zip.file(normalized)) {
        return normalized;
      }
    }
  }
  return undefined;
}

function collectRelationshipTargets(xml: string): Map<string, string> {
  const targets = new Map<string, string>();
  for (const node of collectRelationshipNodes(xml)) {
    if (node.id && node.target) {
      targets.set(node.id, node.target);
    }
  }
  return targets;
}

function collectRelationshipNodes(
  xml: string,
): Array<{ id?: string; target?: string; type?: string }> {
  return collectElementAttributesByLocalName(
    parseOrderedXml(xml),
    'Relationship',
  ).map((attrs) => ({
    id: stringAttribute(attrs, '@_Id'),
    target: stringAttribute(attrs, '@_Target'),
    type: stringAttribute(attrs, '@_Type'),
  }));
}

function normalizePptxTarget(sourcePath: string, target: string): string {
  if (target.startsWith('/')) {
    return pathPosix.normalize(target.slice(1));
  }
  return pathPosix.normalize(
    pathPosix.join(pathPosix.dirname(sourcePath), target),
  );
}

function comparePptxNumberedPaths(left: string, right: string): number {
  return pptxPathNumber(left) - pptxPathNumber(right);
}

function pptxPathNumber(path: string): number {
  const match = /(\d+)\.xml$/u.exec(path);
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
}

function extractPptxTextFromXml(xml: string): string {
  if (!xml.trim()) {
    return '';
  }
  const paragraphs: string[] = [];
  collectPptxParagraphs(parseOrderedXml(xml), paragraphs);
  return paragraphs.join('\n');
}

function collectPptxParagraphs(node: unknown, output: string[]): void {
  if (Array.isArray(node)) {
    for (const child of node) {
      collectPptxParagraphs(child, output);
    }
    return;
  }
  if (!isRecord(node)) {
    return;
  }
  for (const [name, value] of Object.entries(node)) {
    if (!isXmlElementName(name)) {
      continue;
    }
    if (localXmlName(name) === 'p') {
      output.push(...textLinesFromPptxParagraph(value));
      continue;
    }
    collectPptxParagraphs(value, output);
  }
}

function textLinesFromPptxParagraph(node: unknown): string[] {
  const parts: string[] = [];
  collectPptxParagraphText(node, parts);
  return parts
    .join('')
    .split('\n')
    .map((line) => line.replace(/[ \t]+/gu, ' ').trim())
    .filter(Boolean);
}

function collectPptxParagraphText(node: unknown, parts: string[]): void {
  if (Array.isArray(node)) {
    for (const child of node) {
      collectPptxParagraphText(child, parts);
    }
    return;
  }
  if (!isRecord(node)) {
    return;
  }
  for (const [name, value] of Object.entries(node)) {
    if (name === '#text') {
      parts.push(String(value));
      continue;
    }
    if (!isXmlElementName(name)) {
      continue;
    }
    const localName = localXmlName(name);
    if (localName === 'br') {
      parts.push('\n');
      continue;
    }
    if (localName === 'tab') {
      parts.push('\t');
      continue;
    }
    collectPptxParagraphText(value, parts);
  }
}

function collectAttributesByLocalName(
  node: unknown,
  localName: string,
  attributeName: string,
): string[] {
  return collectElementAttributesByLocalName(node, localName)
    .map((attrs) => stringAttribute(attrs, attributeName))
    .filter((value): value is string => value !== undefined);
}

function collectElementAttributesByLocalName(
  node: unknown,
  localName: string,
): Array<Record<string, unknown>> {
  const output: Array<Record<string, unknown>> = [];
  collectElementAttributesByLocalNameInto(node, localName, output);
  return output;
}

function collectElementAttributesByLocalNameInto(
  node: unknown,
  localName: string,
  output: Array<Record<string, unknown>>,
): void {
  if (Array.isArray(node)) {
    for (const child of node) {
      collectElementAttributesByLocalNameInto(child, localName, output);
    }
    return;
  }
  if (!isRecord(node)) {
    return;
  }
  for (const [name, value] of Object.entries(node)) {
    if (name === ':@' || !isXmlElementName(name)) {
      continue;
    }
    if (localXmlName(name) === localName) {
      const attrs = node[':@'];
      output.push(isRecord(attrs) ? attrs : {});
    }
    collectElementAttributesByLocalNameInto(value, localName, output);
  }
}

function parseOrderedXml(xml: string): XmlOrderedNode[] {
  return ORDERED_XML_PARSER.parse(xml) as XmlOrderedNode[];
}

function isXmlElementName(name: string): boolean {
  return name !== ':@' && name !== '#text' && !name.startsWith('@_');
}

function localXmlName(name: string): string {
  return name.includes(':') ? name.slice(name.indexOf(':') + 1) : name;
}

function stringAttribute(
  attrs: Record<string, unknown>,
  name: string,
): string | undefined {
  const value = attrs[name];
  return typeof value === 'string' ? value : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

type PdfProcessResult = {
  status: number | null;
  signal: NodeJS.Signals | null;
  stdout: string;
  stderr: string;
  error?: Error;
  timedOut: boolean;
  outputTruncated: boolean;
};

async function extractPdfTextWithPython({
  bytes,
  pythonCommand,
  timeoutMs,
}: {
  bytes: Buffer;
  pythonCommand: string;
  timeoutMs: number;
}): Promise<string> {
  const result = await runPdfPythonProcess(
    pythonCommand,
    ['-c', PDF_EXTRACT_PYTHON_SOURCE],
    bytes,
    {
      timeoutMs,
      maxOutputBytes: PDF_EXTRACT_MAX_OUTPUT_BYTES,
    },
  );

  if (result.timedOut) {
    throw new UserError(`PDF 文本抽取超时（${timeoutMs}ms）。`);
  }
  if (result.outputTruncated) {
    throw new UserError(
      `PDF 文本抽取结果超过 ${PDF_EXTRACT_MAX_OUTPUT_BYTES} 字节，已中止。`,
    );
  }
  if (result.error) {
    const code = 'code' in result.error ? String(result.error.code) : undefined;
    if (code === 'ENOENT') {
      throw new UserError(
        `PDF 文本抽取需要 Python（${pythonCommand}），但当前环境不可用。`,
      );
    }
    throw result.error;
  }
  if (result.status !== 0) {
    const message =
      result.stderr.trim() || result.stdout.trim() || 'PDF 文本抽取失败';
    throw new UserError(message);
  }

  return result.stdout;
}

async function runPdfPythonProcess(
  command: string,
  args: string[],
  input: Buffer,
  options: {
    timeoutMs: number;
    maxOutputBytes: number;
  },
): Promise<PdfProcessResult> {
  return new Promise((resolve) => {
    const stdoutChunks: Buffer[] = [];
    const stderrChunks: Buffer[] = [];
    let stdoutBytes = 0;
    let stderrBytes = 0;
    let error: Error | undefined;
    let timedOut = false;
    let outputTruncated = false;
    let closed = false;
    let settled = false;
    let timeout: NodeJS.Timeout | undefined;
    let forceKillTimeout: NodeJS.Timeout | undefined;

    const child = spawn(command, args, {
      stdio: ['pipe', 'pipe', 'pipe'],
    });

    const finish = (status: number | null, signal: NodeJS.Signals | null) => {
      if (settled) {
        return;
      }
      settled = true;
      closed = true;
      if (timeout) {
        clearTimeout(timeout);
      }
      if (forceKillTimeout) {
        clearTimeout(forceKillTimeout);
      }
      resolve({
        status,
        signal,
        stdout: Buffer.concat(stdoutChunks).toString('utf8'),
        stderr: Buffer.concat(stderrChunks).toString('utf8'),
        error,
        timedOut,
        outputTruncated,
      });
    };

    const captureChunk = (
      chunks: Buffer[],
      currentBytes: number,
      chunk: Buffer,
    ): number => {
      const remainingBytes = options.maxOutputBytes - currentBytes;
      if (remainingBytes <= 0) {
        outputTruncated = true;
        child.kill('SIGTERM');
        return currentBytes;
      }
      if (chunk.length > remainingBytes) {
        outputTruncated = true;
        chunks.push(chunk.subarray(0, remainingBytes));
        child.kill('SIGTERM');
        return options.maxOutputBytes;
      }
      chunks.push(chunk);
      return currentBytes + chunk.length;
    };

    child.stdout.on('data', (chunk: Buffer) => {
      stdoutBytes = captureChunk(stdoutChunks, stdoutBytes, chunk);
    });
    child.stderr.on('data', (chunk: Buffer) => {
      stderrBytes = captureChunk(stderrChunks, stderrBytes, chunk);
    });

    child.on('error', (childError) => {
      error = childError;
      finish(null, null);
    });

    timeout = setTimeout(() => {
      timedOut = true;
      child.kill('SIGTERM');
      forceKillTimeout = setTimeout(() => {
        if (!closed) {
          child.kill('SIGKILL');
        }
      }, 5_000);
    }, options.timeoutMs);

    child.on('close', (status, signal) => {
      finish(status, signal);
    });

    child.stdin.on('error', () => {
      // If Python exits early (for example missing fitz), stdin may EPIPE. The
      // close/error handlers above still report the real failure to the caller.
    });
    child.stdin.end(input);
  });
}

const PDF_EXTRACT_PYTHON_SOURCE = String.raw`
import sys

try:
    import fitz
except Exception as exc:
    sys.stderr.write("PDF 文本抽取需要 PyMuPDF (fitz)，但当前 Python 环境未安装：" + str(exc))
    sys.exit(10)

data = sys.stdin.buffer.read()

try:
    doc = fitz.open(stream=data, filetype="pdf")
except Exception as exc:
    sys.stderr.write("无法打开 PDF：" + str(exc))
    sys.exit(2)

try:
    if doc.needs_pass:
        sys.stderr.write("PDF 已加密或需要密码，暂不支持读取。")
        sys.exit(3)

    blocks = []
    has_text = False
    for index, page in enumerate(doc, start=1):
        text = page.get_text("text", sort=True)
        text = text.replace("\r\n", "\n").replace("\r", "\n").rstrip()
        if text.strip():
            has_text = True
        blocks.append(f"=== Page {index} ===\n{text}".rstrip())

    if not has_text:
        sys.exit(0)

    sys.stdout.write(("\n\n".join(blocks)).rstrip() + "\n")
finally:
    doc.close()
`;

// function renderImageForTextTransport(output: ViewImageToolResult): string {
//   if (typeof output === "string") {
//     return output;
//   }
//
//   const image = output.image;
//   if (typeof image === "string") {
//     return image;
//   }
//   if (!isRecord(image)) {
//     return "sandbox 会话未返回图片数据。";
//   }
//   const imageRecord = image as Record<string, unknown>;
//   if (typeof imageRecord.url === "string") {
//     return imageRecord.url;
//   }
//   if (typeof imageRecord.fileId === "string") {
//     return `OpenAI 文件引用：${imageRecord.fileId}`;
//   }
//   if (typeof imageRecord.data === "string") {
//     const mediaType =
//       typeof imageRecord.mediaType === "string"
//         ? imageRecord.mediaType
//         : "application/octet-stream";
//     return `data:${mediaType};base64,${imageRecord.data}`;
//   }
//   if (imageRecord.data instanceof Uint8Array) {
//     const mediaType =
//       typeof imageRecord.mediaType === "string"
//         ? imageRecord.mediaType
//         : "application/octet-stream";
//     return `data:${mediaType};base64,${encodeUint8ArrayToBase64(imageRecord.data)}`;
//   }
//
//   return "sandbox 会话未返回图片数据。";
// }
